import dayjs from "dayjs";
import _ from "lodash";

import { colorsPalettes } from "@similarweb/styles";

import { i18nFilter } from "app/filters/i18nFilter";
import combineConfigs from "pro-features/components/Chart/src/combineConfigs";
import dailyIntervalConfig from "pro-features/components/Chart/src/configs/granularity/dailyIntervalConfig";
import monthlyIntervalConfig from "pro-features/components/Chart/src/configs/granularity/monthlyIntervalConfig";
import noLegendConfig from "pro-features/components/Chart/src/configs/legend/noLegendConfig";
import { algoChangeDateConfig } from "pro-features/components/Chart/src/configs/plotLines/algoChangeDateConfig";
import notificationTooltipContainerConfig from "pro-features/components/Chart/src/configs/tooltip/graphBellTooltip/graphBellTooltipContainerConfig";
import tooltipContainerConfig from "pro-features/components/Chart/src/configs/tooltip/tooltipContainerConfig";
import xAxisCrosshair from "pro-features/components/Chart/src/configs/xAxis/xAxisCrosshair";
import xAxisLabelsConfig from "pro-features/components/Chart/src/configs/xAxis/xAxisLabelsConfig";
import yAxisLabelsConfig from "pro-features/components/Chart/src/configs/yAxis/yAxisLabelsConfig";

export const getChartConfig = ({
    metric,
    tooltipFormatter,
    isChartStacking,
    isMarkerEnabled,
    isDailyGranularity,
    isTurboThreshold = false,
    graphData,
    isFromAlternativeSources = false,
    GA4FirstDataPointDate,
}) => {
    const xAxisFormatter = ({ value }) => dayjs.utc(value).format(isDailyGranularity ? "DD MMM" : "MMM YY");
    const currentGranularity = isDailyGranularity ? dailyIntervalConfig : monthlyIntervalConfig;
    const yAxisFormatter = metric.yAxisLabelsFormatter;
    const algoChangeDate = "2020-05-01T00:00:00"; // on alternative sources case we wont show data before 01/05/2020 so we are setting algoChangeDate for the plotLine

    // converting the date stracture for algoChangeDateConfig
    const dateToUTC = (dateString) => {
        const date = dateString.split("-");
        return Date.UTC(parseInt(date[0], 10), parseInt(date[1], 10) - 1, parseInt(date[2], 10));
    };

    return combineConfigs(
        {
            yAxisFormatter,
            xAxisFormatter,
            data: graphData[0]?.data, // if algoChangeDate plotline is removed then this can be deleted.
        },
        [
            notificationTooltipContainerConfig(GA4FirstDataPointDate),
            algoChangeDate && isFromAlternativeSources && graphData.length > 0
                ? algoChangeDateConfig(dateToUTC(algoChangeDate), {
                      showTooltip: true,
                      description: i18nFilter()("website.analysis.traffic.and.engagement.overtime.tooltip.plotline"),
                      overwriteDateRange: true,
                      showDateTitle: false,
                      isPointArray: false,
                  })
                : {},
            currentGranularity,
            noLegendConfig,
            tooltipContainerConfig,
            yAxisLabelsConfig,
            xAxisLabelsConfig,
            xAxisCrosshair,
            metric.tooltipPositioner ? { tooltip: { positioner: metric.POPAndCompareTooltipPositioner } } : {},
            {
                tooltip: {
                    followPointer: false,
                    shared: true,
                    outside: true,
                    useHTML: true,
                    stickOnContact: true,
                    style: {
                        pointerEvents: "auto",
                    },
                    formatter: tooltipFormatter,
                },
                chart: {
                    height: 295,
                    spacingTop: 10,
                    plotBackgroundColor: "transparent",
                    events: {},
                    style: {
                        fontFamily: "Roboto",
                    },
                    animation: false, // no annimation for annotations
                },
                plotOptions: {
                    line: {
                        lineWidth: 2,
                        connectNulls: false,
                        animation: false, // no annimation for annotations
                    },
                    area: {
                        stacking: "normal",
                        animation: false, // no annimation for annotations
                    },
                    series: {
                        fillOpacity: 1,
                        turboThreshold: isTurboThreshold ? 0 : 1000,
                        marker: {
                            enabled: isMarkerEnabled,
                            symbol: "circle",
                        },
                        stacking: isChartStacking,
                        animation: false, // no annimation for annotations
                    },
                },
                yAxis: {
                    gridLineWidth: 0.5,
                    showFirstLabel: true,
                    showLastLabel: true,
                    reversed: false,
                    gridZIndex: 2,
                    reversedStacks: true,
                    tickPixelInterval: 50,
                    labels: {
                        style: {
                            textTransform: "uppercase",
                            fontSize: "11px",
                            fontFamily: "Roboto",
                            color: colorsPalettes.carbon[200],
                        },
                    },
                    max: metric.maxYAxis ?? null,
                },
                xAxis: {
                    gridLineWidth: 0,
                    gridLineDashStyle: "dash",
                    tickLength: 5,
                    labels: {
                        style: {
                            textTransform: "capitalize",
                            fontSize: "11px",
                            color: "#919191",
                            fontFamily: "Roboto",
                            textOverflow: "none",
                            whiteSpace: "nowrap",
                        },
                    },
                    minPadding: 0,
                    maxPadding: 0,
                },
            },
        ],
    );
};

const getTallestColumn = (chart) => {
    return _.maxBy(chart.hoverPoints, (point: any) => point.y);
};

export function POPAndCompareTooltipPositioner(width, height, point) {
    const topPoint = getTallestColumn(this.chart);
    const { top } = topPoint.graphic.element.getBoundingClientRect();
    const leftColumn = this.chart.hoverPoints[0];
    const leftColumnElement = leftColumn.graphic.element;
    const tooltipDimension = {
        xGap: 100,
        yGap: 20,
        height: height,
        width: width,
    };

    if (point.plotX + width > this.chart.plotWidth) {
        tooltipDimension.xGap = point.plotX + width - this.chart.plotWidth;
    }

    if (point.plotY + height < this.chart.plotHeight) {
        tooltipDimension.yGap += point.plotY;
    }

    return {
        x: leftColumnElement.getBoundingClientRect().left - tooltipDimension.xGap,
        y: top - (tooltipDimension.height + tooltipDimension.yGap),
    };
}

// NOTE: may cause tooltip to hide the chart dots, in that case use the positioner above ("POPAndCompareTooltipPositioner").
export function tooltipPositioner(labelWidth, labelHeight, point) {
    const { top, left } = this.chart.clipRect.element.getBoundingClientRect();
    const { plotTop } = this.chart;
    let positionX = left + this.chart.plotLeft + point.plotX;
    if (positionX < 5) {
        positionX = 5;
    } else if (positionX >= window.innerWidth - 5) {
        positionX -= positionX - window.innerWidth + 20;
    }
    return {
        x: Math.min(positionX, this.chart.chartWidth),
        y: top + plotTop,
    };
}
